import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {TPeriodPickerValue} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {CleanFilterButton} from './FiltersButton';
import * as S from './style';

type TLabelProps = {
  resetName?: string;
  resetValue?: string | TAnyObject | null;
  children: React.ReactNode;
};

const FilterLabel = React.memo<TLabelProps>(({resetName, resetValue = null, children}) => (
  <S.FilterLabel>
    {children}
    {resetName && <CleanFilterButton name={resetName} value={resetValue} />}
  </S.FilterLabel>
));

FilterLabel.displayName = 'FilterLabel';

type TFieldProps = TLabelProps & {
  label: React.ReactNode;
};

const FilterField = React.memo<TFieldProps>(({resetName, resetValue = null, children, label}) => (
  <Stack size="2" isFullWidth>
    <FilterLabel resetName={resetName} resetValue={resetValue}>
      {label}
    </FilterLabel>
    {children}
  </Stack>
));

FilterField.displayName = 'FilterField';

type TCheckboxGroupProps = {
  name: string;
  values: string[];
  labelPrefix?: string;
  label?: (value: string) => string;
  columns?: number;
};

const FilterCheckboxGroup = React.memo<TCheckboxGroupProps>(
  ({name, values, labelPrefix, label, columns}) => {
    const {t} = useTranslation();

    const renderValue = React.useCallback(
      (value: string) => (
        <S.CheckboxField noMargin key={value} name={`${name}.${value}`}>
          {labelPrefix ? t(`${labelPrefix}.${value}` as never) : label?.(value)}
        </S.CheckboxField>
      ),
      [labelPrefix, label, name],
    );

    if (columns) {
      return <S.CheckboxGroup columns={columns}>{values.map(renderValue)}</S.CheckboxGroup>;
    }

    return <S.FlatCheckboxGroup>{values.map(renderValue)}</S.FlatCheckboxGroup>;
  },
);

FilterCheckboxGroup.displayName = 'FilterCheckboxGroup';

const FilterPeriodPicker = React.memo<{name: string; className?: string}>(({name, className}) => {
  const {setValue, watch} = useFormContext();

  const period = watch(name);

  const handlePeriodChange = (value: TPeriodPickerValue) => setValue(name, value);

  return (
    <S.PeriodPicker
      className={className}
      onChange={handlePeriodChange}
      value={period}
      showHeaderDate
      needForceClosing
    />
  );
});

FilterPeriodPicker.displayName = 'FilterPeriodPicker';

export {FilterField, FilterPeriodPicker, FilterCheckboxGroup, FilterLabel};
